// @ts-strict-ignore
import type { NodeState, WorkflowNode, WorkflowNodeType } from 'flow/service/graph';
import type { useSnackBar } from '@usealloy/component-library';
import { IconName } from '@usealloy/component-library';
import {
  isActionAnImportedValue, isCustomListAction, isUsingActiveListVersion,
} from 'flow/service/actions';
import {
  NODES_SUPPORTING_CONNECTOR,
  NODES_SUPPORTING_MENU,
  NODE_STATES_SUPPORTING_MENU,
  XL_NODES,
} from './constants';
import type { WorkflowState } from '~/dashboard/views/workflows/store/redux/state';
import type { AppDispatch } from '~/store';
import type { DropdownItem } from './NodeDropdownMenu';
import {
  /* OnCopyDefaults , */ OnDeleteDefaults, OnSwapDefaults, OnUpdateDefaults, OnViewListDefaults,
} from './NodeDropdownMenu';
import type { Action, PublicId } from '~/dashboard/views/workflows/store/engine/types';
import { ActionType } from 'flow/store/engine/constants';
import type { MergeDiffResult } from 'flow/service/changelog/formatters/mergeDropdownFormatter/types';
import { getIsMergeImpacted } from 'flow/service/changelog/formatters/mergeDropdownFormatter/helpers';
import { duplicateNodeThunk } from 'flow/store/redux/thunks';

export function isXLNode(nodeType: WorkflowNodeType) {
  return XL_NODES.has(nodeType);
}

export function doesNodeTypeSupportConnector(
  nodeType: WorkflowNodeType,
): boolean {
  return NODES_SUPPORTING_CONNECTOR.includes(nodeType);
}

export function doesNodeTypeSupportMenu(nodeType: WorkflowNodeType): boolean {
  return NODES_SUPPORTING_MENU.includes(nodeType);
}

export function doesNodeStateSupportMenu(nodeState: NodeState): boolean {
  return NODE_STATES_SUPPORTING_MENU.includes(nodeState);
}

export function showNodeMenu(nodeState: NodeState, nodeType: WorkflowNodeType): boolean {
  if (!doesNodeTypeSupportMenu(nodeType)) {
    return false;
  }

  return doesNodeStateSupportMenu(nodeState);
}

export function canDuplicateNode(nodeType: WorkflowNodeType): boolean {
  return nodeType !== 'snippet' && nodeType !== 'riskCalculation';
}


export function doesActionSupportTooltip(action: Action): boolean {
  if (!action) {
    return false;
  }

  return !isActionAnImportedValue(action);
}

export function getAttributeNodeIcon(args: {
  defaultIcon: IconName;
  isPublishedAttribute: boolean;
  objectType?: 'entity' | 'transaction';
  customTransactionAttributesEnabled?: boolean;
}): IconName {
  const {
    defaultIcon,
    isPublishedAttribute,
    objectType,
    customTransactionAttributesEnabled,
  } = args;

  // Default behavior for getAttributeNodeIcon when feature flag is not set
  if (!customTransactionAttributesEnabled) {
    const iconName = isPublishedAttribute ? IconName.Bookmark : defaultIcon;
    return iconName;
  }

  // Feature flag behavior: Determine icon based on published attribute type
  if (isPublishedAttribute) {
    // Only differentiate by object type if feature flag is enabled
    if (objectType === 'transaction') {
      return IconName.MoneyBracket; // Transaction icon
    }
    return IconName.Person; // Entity icon (default for published attributes)
  }

  return defaultIcon; // Default for non-published attributes
}

export function getDropdownItems({
  addSnackBar,
  dispatch,
  nodeId,
  workflowData,
  setNodeDeletionChildActionCounts,
  nodeType,
  workflowProps,
  onUpdateAction,
  closeRuleTooltip,
  canDuplicate,
  onCopy,
}: {
  addSnackBar: ReturnType<typeof useSnackBar>;
  nodeId: WorkflowNode['id'];
  workflowProps: WorkflowState['workflowProps'];
  workflowData: WorkflowState['workflowData'];
  dispatch: AppDispatch;
  setNodeDeletionChildActionCounts: React.Dispatch<React.SetStateAction<number>>;
  nodeType: WorkflowNodeType;
  onUpdateAction: React.Dispatch<React.SetStateAction<boolean>>;
  closeRuleTooltip: () => void;
  canDuplicate: boolean;
  onCopy?: (arg?: WorkflowNode['id']) => void;
}): DropdownItem[] {
  const action = workflowData?.actions?.[nodeId];
  const customLists = workflowProps?.customLists;

  const isCustomListService = isCustomListAction(action);
  const isAlreadyActive = isCustomListService && isUsingActiveListVersion(action, customLists);

  const dropdownItems: DropdownItem[] = [
    ...(onCopy ? [{
      label: 'Copy',
      onSelect: () => onCopy(nodeId),
      Icon: null,
      style: null,
    }] : []),
    ...(canDuplicate ? [{
      label: 'Duplicate',
      onSelect: () => {
        dispatch(duplicateNodeThunk(nodeId));
        closeRuleTooltip();
      },
      Icon: null,
      style: null,
      dataId: 'alloydd-workflow-duplicate-node',
    }] : []),
    ...(nodeType === ActionType.TAG ? [{
      label: OnSwapDefaults.label,
      onSelect: OnSwapDefaults.onSelect({
        dispatch,
        nodeId,
      }),
      Icon: null,
      style: null,
    }] : []),
    ...(isCustomListService ? [{
      label: OnViewListDefaults.label,
      onSelect: OnViewListDefaults.onSelect({
        nodeId,
        workflowData,
      }),
      Icon: OnViewListDefaults.Icon,
      style: OnViewListDefaults.style,
    }, {
      label: OnUpdateDefaults.label,
      onSelect: OnUpdateDefaults.onSelect({
        onUpdateAction,
      }),
      Icon: null,
      style: null,
      disabled: isAlreadyActive,
    }] : []),
    ...(nodeType === ActionType.SNIPPET ? [{
      label: 'Edit Snippet',
      // TODO: Implement edit snippet
      onSelect: () => {},
      Icon: null,
      style: null,
    }, {
      label: 'Detach from Library',
      // TODO: Implement detach from library
      onSelect: () => {},
      Icon: null,
      style: null,
    }] : []),
    {
      label: OnDeleteDefaults.label,
      onSelect: OnDeleteDefaults.onSelect({
        addSnackBar,
        dispatch,
        nodeId,
        workflowData,
        setNodeDeletionChildActionCounts,
      }),
      Icon: OnDeleteDefaults.Icon,
      style: OnDeleteDefaults.style,
    },
  ];

  return dropdownItems;
}

/**
  * Disallow hover treatment if we are merging and this is not a merge
  * impacted node. Allow otherwise.
  */
export function getCanHoverOnNode(mergeDiff: MergeDiffResult | null, nodeId: PublicId) {
  return !mergeDiff || (mergeDiff && getIsMergeImpacted(mergeDiff, nodeId));
}
